"use client"

import * as React from "react"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { cn } from '@/lib/utils'

import { Button } from "@/components/ui/button"
import { XIcon } from "lucide-react"

/**
 * How many dialogs hold this one, itself counted: 1 for a dialog on the page, 2 for a question
 * opened over a form (the bulk confirmation, "Tinggalkan tanpa menyimpan?", the parts dialog's
 * close question, the move sheet's leave question). Base UI calls a dialog inside another's tree
 * nested and draws no backdrop of its own for it; here a nested one draws a darker backdrop, over
 * the page and over the dialog under it, so only the question in front reads as there to answer.
 */
const KedalamanDialog = React.createContext(0)

/** Counts the dialog it wraps for the dialogs opened inside it; a Sheet counts the same way. */
function LapisDialog({ children }: { children: React.ReactNode }) {
  const kedalaman = React.useContext(KedalamanDialog)
  return (
    <KedalamanDialog.Provider value={kedalaman + 1}>
      {children}
    </KedalamanDialog.Provider>
  )
}

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return (
    <LapisDialog>
      <DialogPrimitive.Root data-slot="dialog" {...props} />
    </LapisDialog>
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

/**
 * The backdrop: ink at 8 % (ink-faint) under a dialog on the page; under a dialog opened over
 * another (`bersarang`), ink at 52 % (ink-soft), the next step of the same ink, drawn even though
 * Base UI leaves a nested dialog's backdrop out by default. It fades as the dialog does.
 */
function DialogOverlay({
  className,
  bersarang = false,
  ...props
}: DialogPrimitive.Backdrop.Props & { bersarang?: boolean }) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      data-bersarang={bersarang ? "" : undefined}
      forceRender={bersarang}
      className={cn(
        "fixed inset-0 isolate z-50 data-open:duration-(--motion-panel-in) data-closed:duration-(--motion-panel-out) data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
        bersarang ? "bg-ink-soft" : "bg-ink-faint",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  const bersarang = React.useContext(KedalamanDialog) > 1
  return (
    <DialogPortal>
      <DialogOverlay bersarang={bersarang} />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-none border border-border bg-popover p-6 text-base text-popover-foreground outline-none data-open:duration-(--motion-panel-in) data-closed:duration-(--motion-panel-out) sm:max-w-md data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button
                variant="ghost"
                className="absolute top-2 right-2"
                size="icon-sm"
              />
            }
          >
            <XIcon
            />
            <span className="sr-only">Tutup</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "-mx-6 -mb-6 flex flex-col-reverse gap-2 border-t bg-band px-6 py-4 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Tutup
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "font-heading text-heading font-semibold",
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "font-prose text-prose text-muted *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-secondary",
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  LapisDialog,
}
